// If you want to use Phoenix channels, run `mix help phx.gen.channel`
// to get started and then uncomment the line below.
// import "./user_socket.js"

// You can include dependencies in two ways.
//
// The simplest option is to put them in assets/vendor and
// import them using relative paths:
//
//     import "../vendor/some-package.js"
//
// Alternatively, you can `npm install some-package --prefix assets` and import
// them using a path starting with the package name:
//
//     import "some-package"
//
// If you have dependencies that try to import CSS, esbuild will generate a separate `app.css` file.
// To load it, simply add a second `<link>` to your `root.html.heex` file.

// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import "phoenix_html"

// Sentry — only loaded when the server emits a <meta name="sentry-dsn">.
// Self-hosted users without a DSN incur no network calls and the dynamic
// import is skipped entirely (esbuild keeps it as a separate chunk).
function initSentry(liveSocket) {
  const sentryDsn = document.querySelector("meta[name='sentry-dsn']")?.content
  if (!sentryDsn) return
  const env = document.querySelector("meta[name='sentry-environment']")?.content
  const release = document.querySelector("meta[name='sentry-release']")?.content
  import("@sentry/browser").then(Sentry => {
    Sentry.init({
      dsn: sentryDsn,
      environment: env || "production",
      release: release || undefined,
      tracesSampleRate: 0,
      // Privacy: never attach personal data, never record DOM/inputs.
      sendDefaultPii: false,
      // Homepage <video autoplay> on iOS Safari/Chrome iOS surfaces these as
      // unhandled errors (play() policy / media element state). Not actionable.
      ignoreErrors: [
        /InvalidStateError/,
        /AbortError/,
        /NotAllowedError/,
      ],
      // Replace the default Breadcrumbs integration with one that doesn't
      // capture console output or DOM text — review/comment content must not leak.
      integrations: defaults => [
        ...defaults.filter(i => i.name !== "Breadcrumbs"),
        Sentry.breadcrumbsIntegration({
          console: false,
          dom: false,
          fetch: true,
          xhr: true,
          history: true,
          sentry: true,
        }),
      ],
      beforeSend(event) {
        // Mermaid/markdown errors quote the offending source verbatim — truncate.
        const ex = event.exception?.values?.[0]
        if (ex?.value && ex.value.length > 200) {
          ex.value = ex.value.slice(0, 200) + "…[truncated]"
        }
        return event
      },
      beforeBreadcrumb(crumb) {
        // Defense in depth — these should already be off via integration config.
        if (["console", "ui.click", "ui.input"].includes(crumb.category)) return null
        return crumb
      },
    })

    // LiveView socket errors hit console.error, not window.onerror.
    // onError fires on every reconnect backoff tick — most are routine
    // mobile WebSocket drops (tab background, lock screen, network handoff)
    // that Phoenix recovers from in milliseconds. Only surface to Sentry
    // when the failed state persists past 5s without a successful reconnect.
    const socket = liveSocket?.getSocket?.()
    if (socket) {
      const PERSISTENCE_MS = 5000
      const MIN_CONSECUTIVE_ERRORS = 3
      let pendingTimer = null
      let consecutiveErrors = 0
      let firstErrorAt = null
      let lastOpenAt = Date.now()
      let lastActivityAt = Date.now()
      let reportedForOutage = false

      const noteActivity = () => { lastActivityAt = Date.now() }
      for (const event of ["pointerdown", "keydown", "touchstart", "focus"]) {
        window.addEventListener(event, noteActivity, { passive: true })
      }
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") noteActivity()
      })

      const resetOutage = () => {
        consecutiveErrors = 0
        firstErrorAt = null
        reportedForOutage = false
        if (pendingTimer !== null) {
          clearTimeout(pendingTimer)
          pendingTimer = null
        }
      }
      window.addEventListener("crit:liveview-connected", resetOutage)

      socket.onError(err => {
        const msg = String(err?.message ?? err)
        if (msg === "410") return
        consecutiveErrors += 1
        if (firstErrorAt === null) firstErrorAt = Date.now()
        if (pendingTimer !== null || reportedForOutage) return
        const errSnapshot = err
        pendingTimer = setTimeout(() => {
          pendingTimer = null
          const msSinceOpen = lastOpenAt ? Date.now() - lastOpenAt : null
          const msSinceActivity = Date.now() - lastActivityAt
          if (consecutiveErrors < MIN_CONSECUTIVE_ERRORS) return
          // Background tabs and offline devices routinely lose their socket.
          // Capture only an active, online session and only once per outage.
          if (document.visibilityState !== "visible" || !document.hasFocus() ||
              !navigator.onLine || msSinceActivity > 30 * 60 * 1000) return
          reportedForOutage = true
          const transport = socket.transportName?.(socket.transport) ||
            (socket.transport === LongPoll ? "longpoll" : "websocket")
          Sentry.captureMessage("LiveSocket transport error (persistent)", {
            level: "warning",
            tags: { liveview_transport: transport },
            extra: {
              type: errSnapshot?.type,
              message: String(errSnapshot?.message ?? errSnapshot),
              consecutive_errors: consecutiveErrors,
              failure_duration_ms: firstErrorAt ? Date.now() - firstErrorAt : null,
              ms_since_last_open: msSinceOpen,
              ms_since_last_activity: msSinceActivity,
              transport,
              visibility_state: document.visibilityState,
              document_has_focus: document.hasFocus(),
              navigator_online: navigator.onLine,
            },
          })
        }, PERSISTENCE_MS)
      })

      socket.onOpen(() => {
        lastOpenAt = Date.now()
        // Review hooks reset only after their LiveView has rejoined. Other
        // pages have no review hook, so transport open is definitive there.
        if (!isReviewPage) resetOutage()
      })
    }
  }).catch(() => {})
}

// Establish Phoenix Socket and LiveView configuration.
import {Socket, LongPoll} from "phoenix"
import {LiveSocket} from "phoenix_live_view"
import {hooks as colocatedHooks} from "phoenix-colocated/crit"
import topbar from "../vendor/topbar"
// Load document-renderer (markdown-it + hljs + mermaid) only on review pages.
// Top-level await is valid in ES modules — it blocks module evaluation so the
// hook is ready synchronously when LiveView calls mounted().
const isReviewPage = window.location.pathname.startsWith('/r/')
const DocumentRendererHook = isReviewPage
  ? (await import("./document-renderer")).DocumentRenderer
  : { mounted() {} }
// PreviewMode chrome — only the preview branch of the review page uses it, but
// it's lazily loaded on any /r/ page (same gate as DocumentRenderer) since the
// review type isn't known client-side until the LiveView mounts.
const PreviewModeHook = isReviewPage
  ? (await import("./preview-mode")).PreviewMode
  : { mounted() {} }

const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content")

// Operators behind WebSocket-incompatible proxies can pin transport to LongPoll
// via CRIT_LIVEVIEW_TRANSPORT=longpoll, surfaced as a meta tag in root.html.heex.
// Pass `transport:` as an option (NOT the positional 4th arg to LiveSocket): the
// positional form silently breaks server-pushed events.
const transportMeta = document.querySelector("meta[name='liveview-transport']")?.content
const transportOpts = transportMeta === "longpoll" ? {transport: LongPoll} : {}

const liveSocket = new LiveSocket("/live", Socket, {
  longPollFallbackMs: 2500,
  params: {_csrf_token: csrfToken},
  hooks: {
    ...colocatedHooks,
    "CritWeb.ReviewLive.DocumentRenderer": DocumentRendererHook,
    "PreviewMode": PreviewModeHook,
  },
  ...transportOpts,
})

// Show progress bar on live navigation and form submits
topbar.config({barColors: {0: "#29d"}, shadowColor: "rgba(0, 0, 0, .3)"})
window.addEventListener("phx:page-loading-start", _info => topbar.show(300))
window.addEventListener("phx:page-loading-stop", _info => topbar.hide())

// Theme switching
function setTheme(theme) {
  if (theme === "system") {
    localStorage.removeItem("phx:theme");
    document.documentElement.removeAttribute("data-theme");
  } else {
    localStorage.setItem("phx:theme", theme);
    document.documentElement.setAttribute("data-theme", theme);
  }
  document.querySelectorAll("[data-phx-theme]").forEach(btn => {
    btn.setAttribute("aria-checked", btn.dataset.phxTheme === theme ? "true" : "false");
  });
}

window.addEventListener("phx:set-theme", e => setTheme(e.target.dataset.phxTheme));
window.addEventListener("storage", e => e.key === "phx:theme" && setTheme(e.newValue || "system"));

// Site header identity popover: close on outside-click / Escape.
// Marketing pages are dead views (controller-rendered), so phx-hook
// won't reliably fire here — use document-level delegation instead.
// The popover open/close itself is handled by phx-click + JS.toggle_attribute,
// which works on dead views too (LiveSocket binds those globally).
document.addEventListener("click", e => {
  const popover = document.getElementById("site-identity-popover")
  const trigger = document.getElementById("site-identity-toggle")
  if (!popover || popover.hidden) return
  if (popover.contains(e.target) || trigger?.contains(e.target)) return
  popover.hidden = true
  trigger?.setAttribute("aria-expanded", "false")
})
document.addEventListener("keydown", e => {
  if (e.key !== "Escape") return
  const popover = document.getElementById("site-identity-popover")
  if (!popover || popover.hidden) return
  popover.hidden = true
  document.getElementById("site-identity-toggle")?.setAttribute("aria-expanded", "false")
})

// connect if there are any LiveViews on the page
liveSocket.connect()
initSentry(liveSocket)

window.addEventListener("clipboard:copy", e => {
  navigator.clipboard.writeText(e.detail.text).catch(() => {})
  const btn = e.target
  const originalText = btn.textContent
  btn.textContent = "✓ Copied"
  setTimeout(() => { btn.textContent = originalText }, 2000)
})

// Close prompt dropdown when clicking outside
document.addEventListener('click', function(e) {
  const dropdown = document.getElementById('prompt-dropdown');
  const splitBtn = document.getElementById('prompt-split-btn');
  if (!dropdown || !splitBtn) return;
  if (!splitBtn.contains(e.target)) {
    dropdown.style.display = 'none';
  }
});

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket

// Unified copy buttons + tab switchers via document-level delegation.
// Marketing pages are dead views patched by <.link navigate>, so direct
// listeners attached at load don't survive DOM swaps. Delegation works
// regardless of when the matching elements appear.
document.addEventListener("click", e => {
  const btn = e.target.closest(".copy-btn")
  if (!btn) return
  if (btn.closest("#empty-state")) return
  const text = btn.dataset.copy
  if (!text) return
  const defaultIcon = btn.querySelector(".icon-default")
  const copiedIcon = btn.querySelector(".icon-copied")
  navigator.clipboard.writeText(text).then(() => {
    btn.style.color = "var(--crit-green)"
    if (defaultIcon && copiedIcon) {
      defaultIcon.classList.add("hidden")
      copiedIcon.classList.remove("hidden")
    }
    setTimeout(() => {
      btn.style.color = ""
      if (defaultIcon && copiedIcon) {
        defaultIcon.classList.remove("hidden")
        copiedIcon.classList.add("hidden")
      }
    }, 2000)
  }).catch(() => {})
})

// Tab switchers: .install-tab / .agent-tab. Each clicked tab activates itself
// and reveals the panel referenced by data-target, hiding sibling panels.
// Sibling tabs/panels are scoped by the shared class (install-* / agent-*).
function activateTab(tab, tabClass, panelClass) {
  const section = tab.closest("section") || tab.closest("div.max-w-md") || tab.parentElement?.parentElement
  const scope = section || document
  scope.querySelectorAll(`.${tabClass}`).forEach(t => {
    t.classList.remove("border-(--crit-brand)", "text-(--crit-brand)")
    t.classList.add("border-transparent", "text-(--crit-fg-muted)")
  })
  scope.querySelectorAll(`.${panelClass}`).forEach(p => p.classList.add("hidden"))
  tab.classList.add("border-(--crit-brand)", "text-(--crit-brand)")
  tab.classList.remove("border-transparent", "text-(--crit-fg-muted)")
  const panel = document.getElementById(tab.dataset.target)
  if (panel) panel.classList.remove("hidden")
}

document.addEventListener("click", e => {
  const installTab = e.target.closest(".install-tab")
  if (installTab && !installTab.closest("#empty-state")) {
    activateTab(installTab, "install-tab", "install-panel")
    return
  }
  const agentTab = e.target.closest(".agent-tab")
  if (agentTab && !agentTab.closest("#empty-state")) {
    activateTab(agentTab, "agent-tab", "agent-panel")
  }
})

// Home page: video modal (open, close, backdrop-click, Escape)
function closeVideoModal() {
  const modal = document.getElementById("video-modal")
  if (modal && !modal.hidden) {
    modal.hidden = true
    document.getElementById("video-iframe").src = ""
  }
}

document.addEventListener("click", (e) => {
  const openBtn = e.target.closest("[data-video-open]")
  if (openBtn) {
    e.preventDefault()
    const modal = document.getElementById("video-modal")
    if (modal) {
      modal.hidden = false
      document.getElementById("video-iframe").src = openBtn.dataset.videoSrc
    }
    return
  }
  if (e.target.closest("[data-video-close]")) return closeVideoModal()
  if (e.target.matches("[data-video-backdrop]")) closeVideoModal()
})

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape") closeVideoModal()
})

// Home page: testimonials scroll-triggered reveal.
// Cards start at opacity-0/translate-y-4 (Tailwind) and the data-revealed
// attribute swaps in opacity-100/translate-y-0 via Tailwind data-[revealed]:* variants.
const testimonialsGrid = document.getElementById("testimonials-grid")
if (testimonialsGrid) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        testimonialsGrid.querySelectorAll("[data-reveal]").forEach((card, i) => {
          setTimeout(() => card.setAttribute("data-revealed", ""), i * 150)
        })
        observer.unobserve(entry.target)
      }
    })
  }, { threshold: 0.1 })
  observer.observe(testimonialsGrid)
}

// (Integration tab bar removed; per-tool pages don't use tabs.)

// Home page: platform stats count-up + staggered reveal
const platformStats = document.getElementById("platform-stats")
if (platformStats) {
  const formatWithCommas = n => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  const formatBytes = bytes => {
    if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`
    if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`
    if (bytes >= 1e3) return `${Math.round(bytes / 1e3)} KB`
    return `${bytes} B`
  }

  const countUp = (el, target, formatter, duration = 1200) => {
    const start = performance.now()
    const step = now => {
      const t = Math.min((now - start) / duration, 1)
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3)
      el.textContent = formatter(Math.round(eased * target))
      if (t < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }

  const statsObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        platformStats.querySelectorAll("[data-count-to]").forEach(el => {
          countUp(el, parseInt(el.dataset.countTo, 10), formatWithCommas)
        })
        platformStats.querySelectorAll("[data-count-bytes]").forEach(el => {
          countUp(el, parseInt(el.dataset.countBytes, 10), formatBytes)
        })
        statsObserver.unobserve(entry.target)
      }
    })
  }, { threshold: 0.2 })
  statsObserver.observe(platformStats)
}

// (.install-tab / .agent-tab handled by document-level delegation above.)

// The lines below enable quality of life phoenix_live_reload
// development features:
//
//     1. stream server logs to the browser console
//     2. click on elements to jump to their definitions in your code editor
//
if (process.env.NODE_ENV === "development") {
  window.addEventListener("phx:live_reload:attached", ({detail: reloader}) => {
    // Enable server log streaming to client.
    // Disable with reloader.disableServerLogs()
    reloader.enableServerLogs()

    // Open configured PLUG_EDITOR at file:line of the clicked element's HEEx component
    //
    //   * click with "c" key pressed to open at caller location
    //   * click with "d" key pressed to open at function component definition location
    let keyDown
    window.addEventListener("keydown", e => keyDown = e.key)
    window.addEventListener("keyup", _e => keyDown = null)
    window.addEventListener("click", e => {
      if(keyDown === "c"){
        e.preventDefault()
        e.stopImmediatePropagation()
        reloader.openEditorAtCaller(e.target)
      } else if(keyDown === "d"){
        e.preventDefault()
        e.stopImmediatePropagation()
        reloader.openEditorAtDef(e.target)
      }
    }, true)

    window.liveReloader = reloader
  })
}

const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

// Lazily load decorative videos as they scroll into view. Skipped under
// reduced-motion — the poster stays visible and nothing autoplays.
if (!prefersReducedMotion) {
  document.querySelectorAll("video[data-lazy-src]").forEach(video => {
    new IntersectionObserver(([entry], obs) => {
      if (!entry.isIntersecting) return
      video.src = video.dataset.lazySrc
      obs.disconnect()
    }, {rootMargin: "200px"}).observe(video)
  })
}

// Under reduced-motion, pause autoplaying videos so only the poster shows.
// The hero keeps its `controls`, so users can still play it on demand.
if (prefersReducedMotion) {
  document.querySelectorAll("video[autoplay]").forEach(video => {
    video.removeAttribute("autoplay")
    video.pause()
  })
}

if (/^\/articles\/[^/]+$/.test(window.location.pathname)) {
  import("./article-mermaid").then(({ initArticleMermaid }) => initArticleMermaid())
}
